import { getEnvironment } from './environment';
import { getUrls } from './url';
import { useUserAgent } from '../hooks/useUserAgent';

export const IOS_URL = 'https://itunes.apple.com/app/id1544666621';
export const ANDROID_URL = 'https://play.google.com/store/apps/details?id=com.class.android';
export const DOWNLOAD_PATH = '/api/zoom/download/';

export interface ILaunchParams {
    stage: string;
    username: string;
    meetingId: string;
    password: string;
    code: string;
    studentId: string;
    zak: string;
}

export interface IDownloadUrls {
    macNextGenUrl: string;
    appDownloadUrl: string;
}

export const groomURL = (url, launchParams: ILaunchParams) => {
    const { username, meetingId, password, code, studentId, zak } = launchParams;

    const { pod, region, account, environment } = getEnvironment();

    const params = new URLSearchParams();
    params.append('account', account);
    params.append('pod_name', pod);
    params.append('pod_region', region);
    params.append('pod_environment', environment);
    params.append('meetingNumber', meetingId);
    params.append('meetingPassword', password);

    if (username && username !== 'null') {
        params.append('displayName', username);
    }

    if (code && code !== 'null') {
        params.append('code', code);
    }

    if (studentId && studentId !== 'null') {
        params.append('student_id', studentId);
    }

    if (zak && zak !== 'null') {
        params.append('zak', zak);
    }

    return `${url}?${params.toString()}`;
};

export const getStage = (stage: string): string => {
    switch (stage) {
        case 'prod':
            return '';
        case 'hipaa':
            return 'healthcare';
        default:
            return 'test';
    }
};

export const getLaunchUrl = (launchParams: ILaunchParams) => {
    const { stage } = launchParams;
    const launchUrl = encodeURI(`classforzoom${getStage(stage)}://open`);
    return groomURL(launchUrl, launchParams);
};

export const desktopLaunch = (launchParams: ILaunchParams) => {
    window.location.replace(getLaunchUrl(launchParams));
};

export const iOSLaunch = (launchParams: ILaunchParams) => {
    window.location.replace(getLaunchUrl(launchParams));

    setTimeout(() => {
        // iOS Fallback to Store
        window.location.replace(IOS_URL);
    }, 2000);
};

export const androidChromebookLaunch = (launchParams: ILaunchParams, chromebook) => {
    const { stage } = launchParams;
    const { environment } = getEnvironment();
    const wellKnownURL = `https://main.rest.global.${stage}.${process.env.REACT_APP_DOMAIN_NAME}`;
    const launchURL = encodeURIComponent(groomURL(`${wellKnownURL}/html/index_mobile.html`, launchParams));

    const afl = 'https%3A%2F%2Fplay.google.com%2Fstore%2Fapps%2Fdetails%3Fid%3Dcom.class.android';
    let packageName = '';
    switch (environment) {
        case 'dev':
            packageName = 'com.class.android.dev';
            break;
        case 'test':
            packageName = 'com.class.android.qa';
            break;
        default:
            packageName = 'com.class.android';
    }
    packageName = encodeURIComponent(packageName);
    const dynamicPageLink = `https://classdynamic.page.link/?link=${launchURL}&apn=${packageName}&afl=${afl}`;

    if (chromebook) {
        window.open(dynamicPageLink, '_blank');
    } else {
        window.location.replace(dynamicPageLink);
    }
};

export const generateDownloadURL = (baseURL: string, platform: string): string =>
    `${baseURL}?${new URLSearchParams({ platform }).toString()}`;

export const useDownloadUrl = (): IDownloadUrls => {
    const { isMac, isAndroid, isIOS, isWindows, isChromebook } = useUserAgent(navigator.userAgent);

    const { api } = getUrls();
    const baseURL = `${api}${DOWNLOAD_PATH}`;
    const macNextGenUrlParams = new URLSearchParams({ platform: 'Mac OS Next Gen' });
    let appDownloadUrl = '';
    if (isMac) {
        appDownloadUrl = generateDownloadURL(baseURL, 'Mac OS');
    } else if (isWindows) {
        appDownloadUrl = generateDownloadURL(baseURL, 'Windows');
    } else if (isChromebook) {
        appDownloadUrl = generateDownloadURL(baseURL, 'Chrome OS');
    } else if (isIOS) {
        appDownloadUrl = IOS_URL;
    } else if (isAndroid) {
        appDownloadUrl = ANDROID_URL;
    }

    return {
        macNextGenUrl: `${baseURL}?${macNextGenUrlParams.toString()}`,
        appDownloadUrl
    };
};
